import {
  TransactionProps,
  TransactionStatus,
  TransactionType,
  OfferingType,
  ChargeBearer,
} from "./transaction.types";

/**
 * Transaction entity.
 * Encapsulates transaction data and business rules.
 *
 * Pure TypeScript - no React imports.
 */
export class Transaction {
  private readonly props: TransactionProps;

  private constructor(props: TransactionProps) {
    this.props = props;
  }

  /**
   * Creates a Transaction entity with validation.
   * @throws Error if required fields are missing or invalid
   */
  static create(props: TransactionProps): Transaction {
    if (props.id === undefined || props.id === null) {
      throw new Error("Transaction id is required");
    }

    if (!props.reference) {
      throw new Error("Transaction reference is required");
    }

    if (props.amount < 0) {
      throw new Error("Transaction amount cannot be negative");
    }

    // Apply defaults for optional fields
    const normalizedProps: TransactionProps = {
      ...props,
      customerName: props.customerName ?? "",
      customerEmail: props.customerEmail ?? "",
      serviceName: props.serviceName ?? null,
      isDiscounted: props.isDiscounted ?? false,
      charges: props.charges ?? 0,
      discount: props.discount ?? 0,
    };

    return new Transaction(normalizedProps);
  }

  // ==================== Getters ====================

  get id(): number {
    return this.props.id;
  }

  get reference(): string {
    return this.props.reference;
  }

  get type(): TransactionType {
    return this.props.type;
  }

  get customerName(): string {
    return this.props.customerName;
  }

  get customerEmail(): string {
    return this.props.customerEmail;
  }

  get serviceName(): string | null {
    return this.props.serviceName;
  }

  get offeringType(): OfferingType {
    return this.props.offeringType;
  }

  get amount(): number {
    return this.props.amount;
  }

  get currency(): string {
    return this.props.currency;
  }

  get status(): TransactionStatus {
    return this.props.status;
  }

  get paymentDate(): string {
    return this.props.paymentDate;
  }

  get transactionId(): number | undefined {
    return this.props.transactionId;
  }

  get sourceTable(): string | undefined {
    return this.props.sourceTable;
  }

  get isDiscounted(): boolean {
    return this.props.isDiscounted ?? false;
  }

  get charges(): number {
    return this.props.charges ?? 0;
  }

  get chargeBearer(): ChargeBearer | undefined {
    return this.props.chargeBearer;
  }

  get discountCode(): string | null | undefined {
    return this.props.discountCode;
  }

  get discount(): number {
    return this.props.discount ?? 0;
  }

  get exchangeRate(): string | undefined {
    return this.props.exchangeRate;
  }

  get convertedPrice(): string | undefined {
    return this.props.convertedPrice;
  }

  // ==================== Business Methods ====================

  /**
   * Check if the transaction is completed (paid/settled).
   */
  isCompleted(): boolean {
    return this.props.status === "COMPLETED";
  }

  /**
   * Check if the transaction is pending (awaiting completion/conversion).
   */
  isPending(): boolean {
    return this.props.status === "PENDING";
  }

  /**
   * Check if this is a foreign currency transaction.
   */
  isForeign(): boolean {
    return this.props.type === "foreign";
  }

  /**
   * Check if this is a local currency transaction.
   */
  isLocal(): boolean {
    return this.props.type === "local";
  }

  /**
   * Returns the raw properties for serialization or mapping.
   */
  toProps(): TransactionProps {
    return {...this.props};
  }
}
